import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { fetchGalleries, fetchGalleryById } from '../services/api';
import { buildGalleryResponsiveSrcSet, getGalleryDisplayImageUrl } from '../utils/images';
import { buildGalleryPath } from '../utils/slug';
import { Gallery as GalleryType } from '../types';

const parseDirectLoginIds = (raw: string | null): string[] => {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.map((value) => String(value)).filter((value) => value.length > 0);
    } catch {
        return [];
    }
};

const Galleries: React.FC = () => {
    const location = useLocation();
    const [galleries, setGalleries] = useState<GalleryType[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadGalleries = async () => {
            try {
                const data = await fetchGalleries();
                const params = new URLSearchParams(location.search);
                const fromDirectLogin = params.get('source') === 'direct-login';

                const directLoginIdsRaw = sessionStorage.getItem('directLoginGalleryIds');
                const directLoginIds = parseDirectLoginIds(directLoginIdsRaw);

                if (!directLoginIds.length) {
                    setGalleries(data);
                    return;
                }

                const privateGalleryResults = await Promise.allSettled(
                    directLoginIds.map((galleryId) => {
                        const token = sessionStorage.getItem(`galleryAccessToken:${galleryId}`);
                        return fetchGalleryById(String(galleryId), token);
                    })
                );

                const privateGalleries: GalleryType[] = [];
                privateGalleryResults.forEach((result) => {
                    if (result.status === 'fulfilled') {
                        privateGalleries.push(result.value);
                    }
                });

                if (fromDirectLogin) {
                    setGalleries(privateGalleries);
                    return;
                }

                const byId = new Map<string, GalleryType>();
                [...privateGalleries, ...data].forEach((gallery: GalleryType) => {
                    byId.set(String(gallery.id), gallery);
                });

                setGalleries(Array.from(byId.values()));
            } catch (err) {
                setError('Failed to load galleries');
            } finally {
                setLoading(false);
            }
        };

        loadGalleries();
    }, [location.search]);

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <div className="public-galleries">
            <header className="public-galleries__header">
                <div>
                    <p className="public-galleries__eyebrow">Collections</p>
                    <h1>Curated Photographic Series</h1>
                    <p>Explore selected collections shaped by light, texture and atmosphere.</p>
                </div>
            </header>
            <div className="public-galleries__grid">
                {galleries.map((gallery, index) => {
                    const coverImage = gallery.images?.[0];
                    const coverImageUrl = getGalleryDisplayImageUrl(coverImage);
                    return (
                        <article key={gallery.id} className="public-gallery-card">
                            <Link to={buildGalleryPath(gallery)} className="public-gallery-card__media">
                                {coverImage ? (
                                    <img
                                        src={coverImageUrl}
                                        srcSet={buildGalleryResponsiveSrcSet(coverImage)}
                                        sizes="(max-width: 768px) 92vw, (max-width: 1200px) 44vw, 30vw"
                                        alt={coverImage.title || gallery.title}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                ) : (
                                    <div className="public-gallery-card__placeholder" aria-hidden="true" />
                                )}
                            </Link>
                            <div className="public-gallery-card__content">
                                <p className="public-gallery-card__index">Series {String(index + 1).padStart(2, '0')}</p>
                                <h2>
                                    <Link to={buildGalleryPath(gallery)}>{gallery.title}</Link>
                                </h2>
                                <p>{gallery.description || 'Kurateret udvalg fra dette galleri.'}</p>
                                <div className="public-gallery-card__meta">
                                    <span>{gallery.images?.length || 0} billeder</span>
                                    {gallery.isPasswordProtected && <span>Beskyttet</span>}
                                </div>
                                <Link className="public-gallery-card__cta" to={buildGalleryPath(gallery)}>
                                    Se galleri
                                </Link>
                            </div>
                        </article>
                    );
                })}
            </div>
        </div>
    );
};

export default Galleries;
